/**
 * Mantine theme, mapped onto the design tokens in DESIGN_GUIDELINES.md §3.1.
 *
 * The important decision here is `primaryShade: 7`. Mantine's default filled button renders
 * white text on the primary shade, and the brand green `#00B140` (green-500) gives white text
 * only 2.85:1 — a WCAG failure at every size. Shade 7 is `#00873C`, which measures 4.63:1 and
 * passes AA for normal text.
 *
 * So: green-500 remains the page backdrop, and green-700 is what buttons and links use.
 */

import { createTheme, type MantineColorsTuple } from '@mantine/core';

/** Green ramp anchored on the brand greens from the palette. */
const hfGreen: MantineColorsTuple = [
  '#e8f7ee', // 0
  '#c6ebd5', // 1
  '#a0deb9', // 2
  '#79d09c', // 3
  '#52c280', // 4
  '#2bb464', // 5
  '#00a650', // 6  green-600
  '#00873c', // 7  green-700 ← primary: white text = 4.63:1 (AA pass)
  '#00702f', // 8  green-800
  '#005a25', // 9
];

const hfPurple: MantineColorsTuple = [
  '#ede9fe',
  '#ddd6fe',
  '#c4b5fd',
  '#a78bfa',
  '#8b6cf0',
  '#7a5ce8',
  '#6c4fe0', // 6  accent (logo, links)
  '#5b3fd0',
  '#4c33b3',
  '#3f2a96',
];

export const theme = createTheme({
  primaryColor: 'hfGreen',
  primaryShade: 7,
  colors: {
    hfGreen,
    hfPurple,
  },
  fontFamily: "'Inter', -apple-system, 'Helvetica Neue', Arial, sans-serif",
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  headings: {
    fontFamily: "'General Sans', 'Inter', system-ui, sans-serif",
    fontWeight: '700',
    sizes: {
      h1: { fontSize: '1.75rem', lineHeight: '1.2' },
      h2: { fontSize: '1.5rem', lineHeight: '1.25' },
      h3: { fontSize: '1.0625rem', lineHeight: '1.3' },
    },
  },
  defaultRadius: 'lg',
  radius: {
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
  },
  cursorType: 'pointer',
  /** Mantine's own focus ring, matched to the purple accent token. */
  focusRing: 'auto',
});
